import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Plus, UtensilsCrossed, LogOut, Edit, Trash2, ExternalLink, Copy, Image, Smartphone, Pencil, Check, X, QrCode, Download, Globe, MapPin, ChevronRight, Building2, ArrowRightLeft, Files, LayoutTemplate, Shield, Play, Clock, Calendar, Layers, GripVertical } from "lucide-react";
import CalendarManager from "@/components/CalendarManager";
import LanguageSelector from "@/components/LanguageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline translation helper for toast messages
const getDashToastMsg = (language, key) => {
  const messages = {
    errorLoadingData: { en: "Error loading data", es: "Error al cargar datos", pt: "Erro ao carregar dados", ht: "Erè nan chajman done" },
    enterLocationName: { en: "Enter location name", es: "Ingresa el nombre de la ubicación", pt: "Digite o nome da localização", ht: "Antre non kote a" },
    errorCreatingLocation: { en: "Error creating location", es: "Error al crear la ubicación", pt: "Erro ao criar localização", ht: "Erè nan kreye kote" },
    errorDeletingLocation: { en: "Error deleting location", es: "Error al eliminar la ubicación", pt: "Erro ao excluir localização", ht: "Erè nan efase kote" },
    errorUpdating: { en: "Error updating", es: "Error al actualizar", pt: "Erro ao atualizar", ht: "Erè nan mete ajou" },
    enterMenuName: { en: "Enter menu name", es: "Ingresa el nombre del menú", pt: "Digite o nome do menu", ht: "Antre non meni an" },
    errorCreatingMenu: { en: "Error creating menu", es: "Error al crear el menú", pt: "Erro ao criar menu", ht: "Erè nan kreye meni" },
    errorDeletingMenu: { en: "Error deleting menu", es: "Error al eliminar el menú", pt: "Erro ao excluir menu", ht: "Erè nan efase meni" },
    errorDuplicatingMenu: { en: "Error duplicating menu", es: "Error al duplicar el menú", pt: "Erro ao duplicar menu", ht: "Erè nan kopi meni" },
    errorMovingMenu: { en: "Error moving menu", es: "Error al mover el menú", pt: "Erro ao mover menu", ht: "Erè nan deplase meni" },
    enterSlideshowName: { en: "Enter slideshow name", es: "Ingresa el nombre del slideshow", pt: "Digite o nome do slideshow", ht: "Antre non slideshow la" },
    selectAtLeastOneMenu: { en: "Select at least one menu", es: "Selecciona al menos un menú", pt: "Selecione pelo menos um menu", ht: "Chwazi omwen yon meni" },
    slideshowCreated: { en: "Slideshow created successfully", es: "Slideshow creado exitosamente", pt: "Slideshow criado com sucesso", ht: "Slideshow kreye avèk siksè" },
    errorCreatingSlideshow: { en: "Error creating slideshow", es: "Error al crear el slideshow", pt: "Erro ao criar slideshow", ht: "Erè nan kreye slideshow" },
    slideshowUpdated: { en: "Slideshow updated", es: "Slideshow actualizado", pt: "Slideshow atualizado", ht: "Slideshow mete ajou" },
    errorUpdatingSlideshow: { en: "Error updating slideshow", es: "Error al actualizar el slideshow", pt: "Erro ao atualizar slideshow", ht: "Erè nan mete ajou slideshow" },
    slideshowDeleted: { en: "Slideshow deleted", es: "Slideshow eliminado", pt: "Slideshow excluído", ht: "Slideshow efase" },
    errorDeletingSlideshow: { en: "Error deleting slideshow", es: "Error al eliminar el slideshow", pt: "Erro ao excluir slideshow", ht: "Erè nan efase slideshow" },
    qrDownloaded: { en: "QR downloaded", es: "QR descargado", pt: "QR baixado", ht: "QR telechaje" },
    schedulesSaved: { en: "Schedules saved", es: "Horario guardado", pt: "Horários salvos", ht: "Orè anrejistre" },
  };
  return messages[key]?.[language] || messages[key]?.en || key;
};

// Predefined menu templates
const MENU_TEMPLATES = [
  {
    id: 'blank',
    name: 'Vacío',
    nameEn: 'Blank',
    namePt: 'Vazio',
    description: 'Lienzo en blanco',
    descriptionEn: 'Blank canvas',
    descriptionPt: 'Tela em branco',
    canvas_width: 1920,
    canvas_height: 1080,
    items: [],
    preview: '📄'
  },
  {
    id: 'restaurant',
    name: 'Restaurante Clásico',
    nameEn: 'Classic Restaurant',
    namePt: 'Restaurante Clássico',
    description: 'Menú con secciones de entradas, platos y postres',
    descriptionEn: 'Menu with starters, mains and desserts',
    descriptionPt: 'Menu com entradas, pratos e sobremesas',
    canvas_width: 1920,
    canvas_height: 1080,
    items: [
      { id: 't1', type: 'name', text: 'MENÚ DEL DÍA', x: 40, y: 3, font_size: 48, font_weight: 700, color: '#ffffff', text_shadow: true, z_index: 1 },
      { id: 't2', type: 'name', text: '— Entradas —', x: 5, y: 15, font_size: 28, font_weight: 600, color: '#f5f5f5', text_shadow: true, z_index: 1 },
      { id: 't3', type: 'name', text: 'Ensalada César', x: 5, y: 22, font_size: 20, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 't4', type: 'price', text: '$8.50', x: 40, y: 22, font_size: 20, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 't5', type: 'name', text: 'Sopa del día', x: 5, y: 28, font_size: 20, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 't6', type: 'price', text: '$6.00', x: 40, y: 28, font_size: 20, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 't7', type: 'name', text: '— Platos Principales —', x: 5, y: 40, font_size: 28, font_weight: 600, color: '#f5f5f5', text_shadow: true, z_index: 1 },
      { id: 't8', type: 'name', text: 'Filete de Res', x: 5, y: 47, font_size: 20, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 't9', type: 'price', text: '$24.00', x: 40, y: 47, font_size: 20, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 't10', type: 'name', text: 'Salmón a la Parrilla', x: 5, y: 53, font_size: 20, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 't11', type: 'price', text: '$22.00', x: 40, y: 53, font_size: 20, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 't12', type: 'name', text: '— Postres —', x: 5, y: 70, font_size: 28, font_weight: 600, color: '#f5f5f5', text_shadow: true, z_index: 1 },
      { id: 't13', type: 'name', text: 'Tiramisú', x: 5, y: 77, font_size: 20, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 't14', type: 'price', text: '$7.50', x: 40, y: 77, font_size: 20, font_weight: 500, color: '#ffd700', z_index: 1 },
    ],
    preview: '🍽️'
  },
  {
    id: 'pizzeria',
    name: 'Pizzería',
    nameEn: 'Pizzeria',
    namePt: 'Pizzaria',
    description: 'Menú para pizzas con tamaños y toppings',
    descriptionEn: 'Pizza menu with sizes and toppings',
    descriptionPt: 'Menu de pizzas com tamanhos e coberturas',
    canvas_width: 1920,
    canvas_height: 1080,
    items: [
      { id: 'p1', type: 'name', text: '🍕 PIZZAS', x: 38, y: 3, font_size: 52, font_weight: 700, color: '#ff6b35', text_shadow: true, z_index: 1 },
      { id: 'p2', type: 'name', text: 'Personal | Mediana | Familiar', x: 28, y: 12, font_size: 18, font_weight: 400, color: '#cccccc', z_index: 1 },
      { id: 'p3', type: 'name', text: 'MARGHERITA', x: 5, y: 22, font_size: 24, font_weight: 600, color: '#ffffff', z_index: 1 },
      { id: 'p4', type: 'price', text: '$8 | $12 | $18', x: 50, y: 22, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'p5', type: 'name', text: 'PEPPERONI', x: 5, y: 32, font_size: 24, font_weight: 600, color: '#ffffff', z_index: 1 },
      { id: 'p6', type: 'price', text: '$9 | $14 | $20', x: 50, y: 32, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'p7', type: 'name', text: 'HAWAIANA', x: 5, y: 42, font_size: 24, font_weight: 600, color: '#ffffff', z_index: 1 },
      { id: 'p8', type: 'price', text: '$9 | $14 | $20', x: 50, y: 42, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'p9', type: 'name', text: '4 QUESOS', x: 5, y: 52, font_size: 24, font_weight: 600, color: '#ffffff', z_index: 1 },
      { id: 'p10', type: 'price', text: '$10 | $15 | $22', x: 50, y: 52, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'p11', type: 'name', text: 'ESPECIAL DE LA CASA', x: 5, y: 62, font_size: 24, font_weight: 600, color: '#ffffff', z_index: 1 },
      { id: 'p12', type: 'price', text: '$12 | $18 | $26', x: 50, y: 62, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
    ],
    preview: '🍕'
  },
  {
    id: 'cafe',
    name: 'Cafetería',
    nameEn: 'Coffee Shop',
    namePt: 'Cafeteria',
    description: 'Menú de bebidas y pasteles',
    descriptionEn: 'Drinks and pastries menu',
    descriptionPt: 'Menu de bebidas e doces',
    canvas_width: 1080,
    canvas_height: 1920,
    items: [
      { id: 'c1', type: 'name', text: '☕ CAFÉ', x: 30, y: 2, font_size: 48, font_weight: 700, color: '#8b4513', text_shadow: true, z_index: 1 },
      { id: 'c2', type: 'name', text: 'Espresso', x: 10, y: 10, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c3', type: 'price', text: '$2.50', x: 70, y: 10, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'c4', type: 'name', text: 'Americano', x: 10, y: 15, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c5', type: 'price', text: '$3.00', x: 70, y: 15, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'c6', type: 'name', text: 'Cappuccino', x: 10, y: 20, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c7', type: 'price', text: '$4.00', x: 70, y: 20, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'c8', type: 'name', text: 'Latte', x: 10, y: 25, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c9', type: 'price', text: '$4.50', x: 70, y: 25, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'c10', type: 'name', text: '🥐 PASTELES', x: 25, y: 35, font_size: 40, font_weight: 700, color: '#8b4513', text_shadow: true, z_index: 1 },
      { id: 'c11', type: 'name', text: 'Croissant', x: 10, y: 42, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c12', type: 'price', text: '$3.50', x: 70, y: 42, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'c13', type: 'name', text: 'Muffin', x: 10, y: 47, font_size: 24, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'c14', type: 'price', text: '$3.00', x: 70, y: 47, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
    ],
    preview: '☕'
  },
  {
    id: 'bar',
    name: 'Bar / Coctelería',
    nameEn: 'Bar / Cocktails',
    namePt: 'Bar / Coquetéis',
    description: 'Menú de cócteles y bebidas',
    descriptionEn: 'Cocktails and drinks menu',
    descriptionPt: 'Menu de coquetéis e bebidas',
    canvas_width: 1920,
    canvas_height: 1080,
    items: [
      { id: 'b1', type: 'name', text: '🍸 COCKTAILS', x: 32, y: 3, font_size: 52, font_weight: 700, color: '#e91e63', text_shadow: true, z_index: 1 },
      { id: 'b2', type: 'name', text: 'Mojito', x: 10, y: 18, font_size: 26, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'b3', type: 'price', text: '$8', x: 45, y: 18, font_size: 24, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'b4', type: 'name', text: 'Margarita', x: 10, y: 28, font_size: 26, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'b5', type: 'price', text: '$9', x: 45, y: 28, font_size: 24, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'b6', type: 'name', text: 'Piña Colada', x: 10, y: 38, font_size: 26, font_weight: 500, color: '#ffffff', z_index: 1 },
      { id: 'b7', type: 'price', text: '$10', x: 45, y: 38, font_size: 24, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'b8', type: 'name', text: '🍺 CERVEZAS', x: 55, y: 18, font_size: 32, font_weight: 600, color: '#ffa500', text_shadow: true, z_index: 1 },
      { id: 'b9', type: 'name', text: 'Nacional', x: 55, y: 28, font_size: 22, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 'b10', type: 'price', text: '$4', x: 85, y: 28, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
      { id: 'b11', type: 'name', text: 'Importada', x: 55, y: 36, font_size: 22, font_weight: 400, color: '#ffffff', z_index: 1 },
      { id: 'b12', type: 'price', text: '$6', x: 85, y: 36, font_size: 22, font_weight: 500, color: '#ffd700', z_index: 1 },
    ],
    preview: '🍸'
  }
];

const DashboardPage = () => {
  const { user, token, logout } = useAuth();
  const { t, language, setLanguage, availableLanguages } = useLanguage();
  const navigate = useNavigate();
  
  // Data states
  const [locations, setLocations] = useState([]);
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selected location filter
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  
  // Create menu dialog
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [newMenuName, setNewMenuName] = useState("");
  const [newMenuLocationId, setNewMenuLocationId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("blank");
  const [isCreatingMenu, setIsCreatingMenu] = useState(false);
  
  // Create location dialog
  const [isCreateLocationOpen, setIsCreateLocationOpen] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationAddress, setNewLocationAddress] = useState("");
  const [newLocationDescription, setNewLocationDescription] = useState("");
  const [isCreatingLocation, setIsCreatingLocation] = useState(false);
  
  // Edit states
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [editingMenuName, setEditingMenuName] = useState("");
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [editingLocationName, setEditingLocationName] = useState("");
  
  // QR dialog
  const [qrMenu, setQrMenu] = useState(null);
  const qrRef = useRef(null);
  
  // Move menu dialog
  const [moveMenu, setMoveMenu] = useState(null);
  const [moveToLocationId, setMoveToLocationId] = useState("");
  
  // Delete location confirmation
  const [locationToDelete, setLocationToDelete] = useState(null);

  // Slideshow states
  const [slideshows, setSlideshows] = useState([]);
  const [isCreateSlideshowOpen, setIsCreateSlideshowOpen] = useState(false);
  const [newSlideshowName, setNewSlideshowName] = useState("");
  const [newSlideshowLocationId, setNewSlideshowLocationId] = useState("");
  const [newSlideshowGlobalDuration, setNewSlideshowGlobalDuration] = useState(10);
  const [newSlideshowTransition, setNewSlideshowTransition] = useState("fade");
  const [selectedMenusForSlideshow, setSelectedMenusForSlideshow] = useState([]);
  const [isCreatingSlideshow, setIsCreatingSlideshow] = useState(false);
  const [editingSlideshow, setEditingSlideshow] = useState(null);
  const [qrSlideshow, setQrSlideshow] = useState(null);
  const qrSlideshowRef = useRef(null);
  
  // Schedule dialog state
  const [scheduleDialog, setScheduleDialog] = useState(null); // {type: 'menu'|'slideshow', id: string, schedule: ScheduleConfig}

  const fetchData = async () => {
    try {
      const [locationsRes, menusRes, slideshowsRes] = await Promise.all([
        axios.get(`${API}/locations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/menus`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/slideshows`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLocations(locationsRes.data);
      setMenus(menusRes.data);
      setSlideshows(slideshowsRes.data);
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorLoadingData'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Location functions
  const handleCreateLocation = async () => {
    if (!newLocationName.trim()) {
      toast.error(getDashToastMsg(language, 'enterLocationName'));
      return;
    }

    setIsCreatingLocation(true);
    try {
      const response = await axios.post(
        `${API}/locations`,
        { 
          name: newLocationName,
          address: newLocationAddress || null,
          description: newLocationDescription || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocations([...locations, response.data]);
      setNewLocationName("");
      setNewLocationAddress("");
      setNewLocationDescription("");
      setIsCreateLocationOpen(false);
      toast.success(t('locationCreated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorCreatingLocation'));
    } finally {
      setIsCreatingLocation(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;
    try {
      await axios.delete(`${API}/locations/${locationToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(locations.filter(l => l.id !== locationToDelete.id));
      // Update menus that were in this location
      setMenus(menus.map(m => m.location_id === locationToDelete.id ? { ...m, location_id: null } : m));
      if (selectedLocationId === locationToDelete.id) {
        setSelectedLocationId(null);
      }
      toast.success(t('locationDeleted'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorDeletingLocation'));
    } finally {
      setLocationToDelete(null);
    }
  };

  const handleSaveLocationName = async (locationId) => {
    if (!editingLocationName.trim()) return;
    try {
      await axios.put(
        `${API}/locations/${locationId}`,
        { name: editingLocationName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLocations(locations.map(l => 
        l.id === locationId ? { ...l, name: editingLocationName.trim() } : l
      ));
      setEditingLocationId(null);
      toast.success(t('nameUpdated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorUpdating'));
    }
  };

  // Menu functions
  const handleCreateMenu = async () => {
    if (!newMenuName.trim()) {
      toast.error(getDashToastMsg(language, 'enterMenuName'));
      return;
    }

    setIsCreatingMenu(true);
    try {
      // Get template data
      const template = MENU_TEMPLATES.find(t => t.id === selectedTemplate) || MENU_TEMPLATES[0];
      
      // Generate new IDs for template items
      const itemsWithNewIds = template.items.map(item => ({
        ...item,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        font_family: item.font_family || 'Arial'
      }));
      
      const response = await axios.post(
        `${API}/menus`,
        { 
          name: newMenuName,
          location_id: newMenuLocationId || null,
          items: itemsWithNewIds,
          canvas_width: template.canvas_width,
          canvas_height: template.canvas_height
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMenus([...menus, response.data]);
      setNewMenuName("");
      setNewMenuLocationId("");
      setSelectedTemplate("blank");
      setIsCreateMenuOpen(false);
      toast.success(t('menuCreated'));
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorCreatingMenu'));
    } finally {
      setIsCreatingMenu(false);
    }
  };

  const handleDeleteMenu = async (menuId) => {
    try {
      await axios.delete(`${API}/menus/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenus(menus.filter(m => m.id !== menuId));
      toast.success(t('menuDeleted'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorDeletingMenu'));
    }
  };

  const handleDuplicateMenu = async (menuId) => {
    try {
      const response = await axios.post(
        `${API}/menus/${menuId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMenus([...menus, response.data]);
      toast.success(t('menuDuplicated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorDuplicatingMenu'));
    }
  };

  const handleSaveMenuName = async (menuId) => {
    if (!editingMenuName.trim()) return;
    try {
      await axios.put(
        `${API}/menus/${menuId}`,
        { name: editingMenuName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMenus(menus.map(m => 
        m.id === menuId ? { ...m, name: editingMenuName.trim() } : m
      ));
      setEditingMenuId(null);
      toast.success(t('nameUpdated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorUpdating'));
    }
  };

  const copyPublicLink = (menuId) => {
    const url = `${window.location.origin}/menu/${menuId}`;
    navigator.clipboard.writeText(url);
    toast.success(t('linkCopied'));
  };

  const handleMoveMenu = async () => {
    if (!moveMenu) return;
    
    try {
      await axios.put(
        `${API}/menus/${moveMenu.id}`,
        { location_id: moveToLocationId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMenus(menus.map(m => 
        m.id === moveMenu.id ? { ...m, location_id: moveToLocationId || null } : m
      ));
      setMoveMenu(null);
      setMoveToLocationId("");
      toast.success(t('menuMoved'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorMovingMenu'));
    }
  };

  // ============ SLIDESHOW FUNCTIONS ============
  
  const handleCreateSlideshow = async () => {
    if (!newSlideshowName.trim()) {
      toast.error(getDashToastMsg(language, 'enterSlideshowName'));
      return;
    }
    if (selectedMenusForSlideshow.length === 0) {
      toast.error(getDashToastMsg(language, 'selectAtLeastOneMenu'));
      return;
    }

    setIsCreatingSlideshow(true);
    try {
      const response = await axios.post(
        `${API}/slideshows`,
        {
          name: newSlideshowName,
          location_id: newSlideshowLocationId || null,
          menus: selectedMenusForSlideshow.map(m => ({
            menu_id: m.id,
            duration: m.duration || null
          })),
          global_duration: newSlideshowGlobalDuration,
          transition_effect: newSlideshowTransition
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlideshows([...slideshows, response.data]);
      setIsCreateSlideshowOpen(false);
      setNewSlideshowName("");
      setNewSlideshowLocationId("");
      setSelectedMenusForSlideshow([]);
      setNewSlideshowGlobalDuration(10);
      setNewSlideshowTransition("fade");
      toast.success(getDashToastMsg(language, 'slideshowCreated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorCreatingSlideshow'));
    } finally {
      setIsCreatingSlideshow(false);
    }
  };

  const handleUpdateSlideshow = async () => {
    if (!editingSlideshow) return;
    
    try {
      const response = await axios.put(
        `${API}/slideshows/${editingSlideshow.id}`,
        {
          name: editingSlideshow.name,
          location_id: editingSlideshow.location_id,
          menus: editingSlideshow.menus,
          global_duration: editingSlideshow.global_duration,
          transition_effect: editingSlideshow.transition_effect,
          schedule: editingSlideshow.schedule
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlideshows(slideshows.map(s => s.id === response.data.id ? response.data : s));
      setEditingSlideshow(null);
      toast.success(getDashToastMsg(language, 'slideshowUpdated'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorUpdatingSlideshow'));
    }
  };

  const handleDeleteSlideshow = async (slideshowId) => {
    try {
      await axios.delete(`${API}/slideshows/${slideshowId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlideshows(slideshows.filter(s => s.id !== slideshowId));
      toast.success(getDashToastMsg(language, 'slideshowDeleted'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorDeletingSlideshow'));
    }
  };

  const addMenuToSlideshow = (menu) => {
    if (!selectedMenusForSlideshow.find(m => m.id === menu.id)) {
      setSelectedMenusForSlideshow([...selectedMenusForSlideshow, { ...menu, duration: null }]);
    }
  };

  const removeMenuFromSlideshow = (menuId) => {
    setSelectedMenusForSlideshow(selectedMenusForSlideshow.filter(m => m.id !== menuId));
  };

  const updateMenuDurationInSlideshow = (menuId, duration) => {
    setSelectedMenusForSlideshow(selectedMenusForSlideshow.map(m => 
      m.id === menuId ? { ...m, duration: duration || null } : m
    ));
  };

  const moveMenuInSlideshow = (index, direction) => {
    const newList = [...selectedMenusForSlideshow];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newList.length) return;
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setSelectedMenusForSlideshow(newList);
  };

  const getSlideshowPublicUrl = (slideshowId) => `${window.location.origin}/slideshow/${slideshowId}`;

  const downloadSlideshowQR = () => {
    if (!qrSlideshowRef.current || !qrSlideshow) return;
    
    const svg = qrSlideshowRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-slideshow-${qrSlideshow.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success(getDashToastMsg(language, 'qrDownloaded'));
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // ============ SCHEDULE FUNCTIONS ============
  
  const handleSaveSchedule = async () => {
    if (!scheduleDialog) return;
    
    try {
      if (scheduleDialog.type === 'menu') {
        await axios.put(
          `${API}/menus/${scheduleDialog.id}`,
          { schedule: scheduleDialog.schedule },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMenus(menus.map(m => m.id === scheduleDialog.id ? { ...m, schedule: scheduleDialog.schedule } : m));
      } else {
        await axios.put(
          `${API}/slideshows/${scheduleDialog.id}`,
          { schedule: scheduleDialog.schedule },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSlideshows(slideshows.map(s => s.id === scheduleDialog.id ? { ...s, schedule: scheduleDialog.schedule } : s));
      }
      setScheduleDialog(null);
      toast.success(getDashToastMsg(language, 'schedulesSaved'));
    } catch (error) {
      toast.error(getDashToastMsg(language, 'errorSavingSchedule') || "Error saving schedule");
    }
  };

  const copyMobileLink = () => {
    const url = `${window.location.origin}/mobile`;
    navigator.clipboard.writeText(url);
    toast.success(t('mobileLinkCopied'));
  };

  const getPublicUrl = (menuId) => `${window.location.origin}/menu/${menuId}`;

  const downloadQR = () => {
    if (!qrRef.current || !qrMenu) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    
    img.onload = () => {
      canvas.width = 300;
      canvas.height = 300;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-${qrMenu.name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      toast.success(t('qrDownloaded'));
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Filter menus based on selected location
  const filteredMenus = selectedLocationId 
    ? menus.filter(m => m.location_id === selectedLocationId)
    : menus;

  // Filter slideshows based on selected location
  const filteredSlideshows = selectedLocationId 
    ? slideshows.filter(s => s.location_id === selectedLocationId)
    : slideshows;

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || t('allMenus');
  };

  // Menu card component
  const MenuCard = ({ menu }) => (
    <Card 
      className="border-stone-200 hover:shadow-md transition-shadow duration-200 group"
      data-testid={`menu-card-${menu.id}`}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-stone-900 flex items-center justify-between">
          {editingMenuId === menu.id ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editingMenuName}
                onChange={(e) => setEditingMenuName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveMenuName(menu.id);
                  if (e.key === "Escape") setEditingMenuId(null);
                }}
                className="h-7 text-base font-semibold flex-1"
                autoFocus
              />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={() => handleSaveMenuName(menu.id)}>
                <Check className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-stone-400" onClick={() => setEditingMenuId(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div 
                className="flex items-center gap-2 cursor-pointer hover:text-stone-700"
                onClick={() => { setEditingMenuId(menu.id); setEditingMenuName(menu.name); }}
              >
                {menu.name}
                <Pencil className="w-3 h-3 text-stone-400 opacity-0 group-hover:opacity-100" />
              </div>
              <span className="text-xs font-normal text-stone-400">{menu.items?.length || 0} {t('items')}</span>
            </>
          )}
        </CardTitle>
        <CardDescription className="text-stone-500 flex items-center gap-2">
          {menu.location_id && (
            <span className="flex items-center gap-1 text-xs bg-stone-100 px-2 py-0.5 rounded">
              <MapPin className="w-3 h-3" />
              {getLocationName(menu.location_id)}
            </span>
          )}
          <span>{t('updated')}: {new Date(menu.updated_at).toLocaleDateString()}</span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div 
          className="h-32 rounded-md mb-4 bg-stone-800 flex items-center justify-center overflow-hidden"
          style={menu.background_image ? {
            backgroundImage: `url(${menu.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}}
        >
          {!menu.background_image && <Image className="w-8 h-8 text-stone-600" />}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="flex-1 min-w-0" onClick={() => navigate(`/editor/${menu.id}`)}>
            <Edit className="w-4 h-4 mr-1 flex-shrink-0" /> <span className="truncate">{t('edit')}</span>
          </Button>
          <Button variant="outline" size="sm" className="flex-shrink-0 w-9 h-9 p-0" onClick={() => copyPublicLink(menu.id)} title={t('copyPublicLink')}>
            <Copy className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="flex-shrink-0 w-9 h-9 p-0" onClick={() => setQrMenu(menu)} title={t('generateQR')}>
            <QrCode className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="flex-shrink-0 w-9 h-9 p-0" onClick={() => window.open(`/menu/${menu.id}`, '_blank')} title={t('openPublicLink')}>
            <ExternalLink className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-shrink-0 w-9 h-9 p-0" 
            onClick={() => handleDuplicateMenu(menu.id)}
            title={t('duplicateMenu')}
          >
            <Files className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-shrink-0 w-9 h-9 p-0" 
            onClick={() => { setMoveMenu(menu); setMoveToLocationId(menu.location_id || ""); }}
            title={t('moveMenu')}
          >
            <ArrowRightLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" className="flex-shrink-0 w-9 h-9 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDeleteMenu(menu.id)} title={t('deleteMenu')}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/assets/logo-sally.png" 
              alt="Sally Better" 
              className="h-10 w-auto invert"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-600">{t('hello')}, {user?.name}</span>
            <Button variant="outline" size="sm" onClick={copyMobileLink}>
              <Smartphone className="w-4 h-4 mr-2" /> {t('mobileLink')}
            </Button>
            {user?.role === "admin" && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate("/admin")}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Shield className="w-4 h-4 mr-2" /> Admin
              </Button>
            )}
            <LanguageSelector variant="dark" showLabel={true} />
            <Button variant="outline" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" /> {t('logout')}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar - Locations */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg border border-stone-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> {t('locations')}
                </h3>
                <Dialog open={isCreateLocationOpen} onOpenChange={setIsCreateLocationOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('newLocation')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>{t('locationName')} *</Label>
                        <Input
                          value={newLocationName}
                          onChange={(e) => setNewLocationName(e.target.value)}
                          placeholder="Restaurante Centro..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('address')}</Label>
                        <Input
                          value={newLocationAddress}
                          onChange={(e) => setNewLocationAddress(e.target.value)}
                          placeholder="Calle Principal #123..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('description')}</Label>
                        <Textarea
                          value={newLocationDescription}
                          onChange={(e) => setNewLocationDescription(e.target.value)}
                          placeholder="Sucursal principal..."
                          rows={2}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsCreateLocationOpen(false)}>{t('cancel')}</Button>
                      <Button onClick={handleCreateLocation} disabled={isCreatingLocation}>
                        {isCreatingLocation ? t('creating') : t('createLocation')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-1">
                {/* All menus option */}
                <button
                  onClick={() => setSelectedLocationId(null)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between group ${
                    selectedLocationId === null ? 'bg-stone-900 text-white' : 'hover:bg-stone-100 text-stone-700'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <UtensilsCrossed className="w-4 h-4" />
                    {t('allMenus')}
                  </span>
                  <span className="text-xs opacity-70">{menus.length}</span>
                </button>

                {/* Location list */}
                {locations.map((location) => {
                  const menuCount = menus.filter(m => m.location_id === location.id).length;
                  const isSelected = selectedLocationId === location.id;
                  const isEditing = editingLocationId === location.id;
                  
                  return (
                    <div key={location.id} className="group relative">
                      {isEditing ? (
                        <div className="flex items-center gap-1 px-2 py-1">
                          <Input
                            value={editingLocationName}
                            onChange={(e) => setEditingLocationName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveLocationName(location.id);
                              if (e.key === "Escape") setEditingLocationId(null);
                            }}
                            className="h-7 text-sm"
                            autoFocus
                          />
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleSaveLocationName(location.id)}>
                            <Check className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setSelectedLocationId(location.id)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between ${
                            isSelected ? 'bg-stone-900 text-white' : 'hover:bg-stone-100 text-stone-700'
                          }`}
                        >
                          <span className="flex items-center gap-2 truncate">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{location.name}</span>
                          </span>
                          <span className="text-xs opacity-70 flex-shrink-0">{menuCount}</span>
                        </button>
                      )}
                      
                      {/* Edit/Delete buttons on hover */}
                      {!isEditing && (
                        <div className={`absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5 ${isSelected ? '' : ''}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingLocationId(location.id);
                              setEditingLocationName(location.name);
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocationToDelete(location);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main content - Menus */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-stone-900">
                  {selectedLocationId ? getLocationName(selectedLocationId) : t('myMenus')}
                </h2>
                <p className="text-stone-500 mt-1">
                  {selectedLocationId ? t('menusInLocation') : t('manageMenus')}
                </p>
              </div>
              
              <Dialog open={isCreateMenuOpen} onOpenChange={setIsCreateMenuOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-stone-900 hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-2" /> {t('newMenu')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-xl">
                  <DialogHeader>
                    <DialogTitle>{t('createMenu')}</DialogTitle>
                    <DialogDescription>{t('selectTemplate')}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{t('menuName')}</Label>
                      <Input
                        value={newMenuName}
                        onChange={(e) => setNewMenuName(e.target.value)}
                        placeholder="Menú Principal..."
                        data-testid="menu-name-input"
                      />
                    </div>
                    
                    {/* Template selection */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <LayoutTemplate className="w-4 h-4" /> {t('template')}
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {MENU_TEMPLATES.map((template) => {
                          const templateName = language === 'en' ? template.nameEn : 
                                              language === 'pt' ? template.namePt : template.name;
                          const templateDesc = language === 'en' ? template.descriptionEn : 
                                               language === 'pt' ? template.descriptionPt : template.description;
                          return (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => setSelectedTemplate(template.id)}
                              className={`p-3 rounded-lg border-2 text-left transition-all hover:border-stone-400 ${
                                selectedTemplate === template.id 
                                  ? 'border-stone-900 bg-stone-50' 
                                  : 'border-stone-200'
                              }`}
                            >
                              <div className="text-2xl mb-1">{template.preview}</div>
                              <div className="font-medium text-sm text-stone-900">{templateName}</div>
                              <div className="text-xs text-stone-500 line-clamp-1">{templateDesc}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>{t('locations')}</Label>
                      <Select value={newMenuLocationId || "none"} onValueChange={(v) => setNewMenuLocationId(v === "none" ? "" : v)}>
                        <SelectTrigger>
                          <SelectValue placeholder={t('noLocation')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- {t('noLocation')} --</SelectItem>
                          {locations.map((loc) => (
                            <SelectItem key={loc.id} value={loc.id}>
                              <span className="flex items-center gap-2">
                                <MapPin className="w-3 h-3" /> {loc.name}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateMenuOpen(false)}>{t('cancel')}</Button>
                    <Button onClick={handleCreateMenu} disabled={isCreatingMenu} data-testid="confirm-create-menu-btn">
                      {isCreatingMenu ? t('creating') : t('createMenu')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Menu grid */}
            {isLoading ? (
              <div className="text-center py-12 text-stone-500">{t('loading')}</div>
            ) : filteredMenus.length === 0 ? (
              <Card className="border-dashed border-stone-300">
                <CardContent className="py-12 text-center">
                  <UtensilsCrossed className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">
                    {selectedLocationId ? t('noMenusInLocation') : t('noMenusYet')}
                  </h3>
                  <p className="text-stone-500 mb-4">{t('createFirstMenu')}</p>
                  <Button onClick={() => setIsCreateMenuOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> {t('newMenu')}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredMenus.map((menu) => (
                  <MenuCard key={menu.id} menu={menu} />
                ))}
              </div>
            )}

            {/* Slideshows Section */}
            <div className="mt-12 border-t border-stone-200 pt-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2">
                    <Layers className="w-6 h-6" /> {t('slideshows') || 'Slideshows'}
                  </h2>
                  <p className="text-stone-500 mt-1">
                    {t('slideshowsDesc')}
                  </p>
                </div>
                
                <Dialog open={isCreateSlideshowOpen} onOpenChange={setIsCreateSlideshowOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" /> {t('newSlideshow')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{t('createSlideshow')}</DialogTitle>
                      <DialogDescription>{t('createSlideshowDesc')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('slideshowName')}</Label>
                          <Input
                            value={newSlideshowName}
                            onChange={(e) => setNewSlideshowName(e.target.value)}
                            placeholder={language === 'en' ? 'Main Slideshow...' : language === 'pt' ? 'Slideshow Principal...' : language === 'ht' ? 'Slideshow Prensipal...' : 'Slideshow Principal...'}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('locationOptional')}</Label>
                          <Select value={newSlideshowLocationId || "none"} onValueChange={(v) => setNewSlideshowLocationId(v === "none" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('noLocation') || (language === 'en' ? 'No location' : language === 'pt' ? 'Sem localização' : language === 'ht' ? 'San kote' : 'Sin ubicación')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-- {language === 'en' ? 'No location' : language === 'pt' ? 'Sem localização' : language === 'ht' ? 'San kote' : 'Sin ubicación'} --</SelectItem>
                              {locations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>
                                  <span className="flex items-center gap-2">
                                    <MapPin className="w-3 h-3" /> {loc.name}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {t('globalDuration')}
                          </Label>
                          <div className="flex items-center gap-3">
                            <Slider
                              value={[newSlideshowGlobalDuration]}
                              onValueChange={([v]) => setNewSlideshowGlobalDuration(v)}
                              min={5}
                              max={60}
                              step={5}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium w-8">{newSlideshowGlobalDuration}s</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>{t('transitionEffect')}</Label>
                          <Select value={newSlideshowTransition} onValueChange={setNewSlideshowTransition}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fade">{t('fade')}</SelectItem>
                              <SelectItem value="slide">{t('slide')}</SelectItem>
                              <SelectItem value="none">{t('noTransition')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Menu selection */}
                      <div className="space-y-2">
                        <Label>{t('availableMenus')}</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg bg-stone-50">
                          {menus.filter(m => !selectedMenusForSlideshow.find(s => s.id === m.id)).map((menu) => (
                            <button
                              key={menu.id}
                              type="button"
                              onClick={() => addMenuToSlideshow(menu)}
                              className="p-2 rounded-lg border bg-white hover:bg-blue-50 hover:border-blue-300 text-left transition-all text-sm"
                            >
                              <div className="font-medium truncate">{menu.name}</div>
                              <div className="text-xs text-stone-500">
                                {menu.items?.length || 0} items
                              </div>
                            </button>
                          ))}
                          {menus.filter(m => !selectedMenusForSlideshow.find(s => s.id === m.id)).length === 0 && (
                            <p className="col-span-full text-sm text-stone-400 text-center py-2">
                              {t('noMoreMenusAvailable')}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Selected menus (sortable) */}
                      <div className="space-y-2">
                        <Label>{t('menusInSlideshow')} ({selectedMenusForSlideshow.length})</Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                          {selectedMenusForSlideshow.length === 0 ? (
                            <p className="text-sm text-stone-400 text-center py-4">
                              {t('selectMenusToAdd')}
                            </p>
                          ) : (
                            selectedMenusForSlideshow.map((menu, index) => (
                              <div key={menu.id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex flex-col gap-0.5">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => moveMenuInSlideshow(index, -1)}
                                    disabled={index === 0}
                                  >
                                    <ChevronRight className="w-3 h-3 rotate-[-90deg]" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0"
                                    onClick={() => moveMenuInSlideshow(index, 1)}
                                    disabled={index === selectedMenusForSlideshow.length - 1}
                                  >
                                    <ChevronRight className="w-3 h-3 rotate-90" />
                                  </Button>
                                </div>
                                <GripVertical className="w-4 h-4 text-stone-400" />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{menu.name}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    placeholder="Global"
                                    value={menu.duration || ""}
                                    onChange={(e) => updateMenuDurationInSlideshow(menu.id, parseInt(e.target.value) || null)}
                                    className="w-20 h-7 text-xs"
                                  />
                                  <span className="text-xs text-stone-500">seg</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                  onClick={() => removeMenuFromSlideshow(menu.id)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsCreateSlideshowOpen(false)}>
                        {t('cancel')}
                      </Button>
                      <Button 
                        onClick={handleCreateSlideshow} 
                        disabled={isCreatingSlideshow}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {isCreatingSlideshow ? t('creating') : t('createSlideshow')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Slideshows grid */}
              {filteredSlideshows.length === 0 ? (
                <Card className="border-dashed border-2 border-stone-300 bg-stone-50/50">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Layers className="w-12 h-12 text-stone-300 mb-4" />
                    <p className="text-stone-500 text-center mb-4">
                      {t('noSlideshowsYet')}
                    </p>
                    <Button 
                      onClick={() => setIsCreateSlideshowOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" /> {t('createFirstSlideshow')}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSlideshows.map((slideshow) => (
                    <Card key={slideshow.id} className="border-blue-200 hover:shadow-md transition-shadow duration-200 group">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg text-stone-900 flex items-center justify-between">
                          <span className="flex items-center gap-2 truncate">
                            <Layers className="w-5 h-5 text-blue-600" />
                            {slideshow.name}
                          </span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => setQrSlideshow(slideshow)}
                              title="Ver QR"
                            >
                              <QrCode className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => window.open(getSlideshowPublicUrl(slideshow.id), "_blank")}
                              title="Ver slideshow"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 text-xs">
                          <Play className="w-3 h-3" />
                          {slideshow.menus?.length || 0} {language === 'en' ? 'menus' : language === 'pt' ? 'menus' : language === 'ht' ? 'meni' : 'menús'} • {slideshow.global_duration}s
                          {slideshow.schedule?.enabled && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Calendar className="w-3 h-3" /> {t('scheduled')}
                            </span>
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {/* Menu list preview */}
                        <div className="space-y-1 mb-4">
                          {slideshow.menus?.slice(0, 3).map((menuItem, idx) => {
                            const menu = menus.find(m => m.id === menuItem.menu_id);
                            return menu ? (
                              <div key={menuItem.menu_id} className="flex items-center justify-between text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded">
                                <span className="truncate">{idx + 1}. {menu.name}</span>
                                <span className="text-stone-400">{menuItem.duration || slideshow.global_duration}s</span>
                              </div>
                            ) : null;
                          })}
                          {(slideshow.menus?.length || 0) > 3 && (
                            <div className="text-xs text-stone-400 text-center">
                              +{slideshow.menus.length - 3} más
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => setEditingSlideshow({...slideshow})}
                          >
                            <Edit className="w-4 h-4 mr-1" /> {t('edit')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => setScheduleDialog({
                              type: 'slideshow',
                              id: slideshow.id,
                              schedule: slideshow.schedule || { enabled: false, days: [], day_type: 'weekdays', start_time: '00:00', end_time: '23:59', fallback_message: 'Vuelve pronto' }
                            })}
                          >
                            <Calendar className="w-4 h-4 mr-1" /> {t('schedule')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteSlideshow(slideshow.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Calendars Section */}
            <div className="mt-12 border-t border-stone-200 pt-8">
              <CalendarManager 
                menus={menus} 
                slideshows={slideshows} 
                token={localStorage.getItem('token')}
                language={language}
              />
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={!!qrMenu} onOpenChange={(open) => !open && setQrMenu(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" /> {t('qrCode')} - {qrMenu?.name}
            </DialogTitle>
            <DialogDescription>{t('scanToView')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div ref={qrRef} className="p-4 bg-white rounded-lg border shadow-sm">
              {qrMenu && <QRCodeSVG value={getPublicUrl(qrMenu.id)} size={200} level="H" includeMargin />}
            </div>
            <p className="text-xs text-stone-500 text-center break-all max-w-[250px]">
              {qrMenu && getPublicUrl(qrMenu.id)}
            </p>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-center">
            <Button variant="outline" onClick={() => { if (qrMenu) { navigator.clipboard.writeText(getPublicUrl(qrMenu.id)); toast.success(t('linkCopied')); } }} className="flex-1">
              <Copy className="w-4 h-4 mr-2" /> {t('copyLink')}
            </Button>
            <Button onClick={downloadQR} className="flex-1 bg-stone-900 hover:bg-stone-800">
              <Download className="w-4 h-4 mr-2" /> {t('downloadQR')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Menu Dialog */}
      <Dialog open={!!moveMenu} onOpenChange={(open) => !open && setMoveMenu(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" /> {t('moveMenu')}
            </DialogTitle>
            <DialogDescription>
              {t('moveMenuDescription')}: <strong>{moveMenu?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="mb-2 block">{t('selectNewLocation')}</Label>
            <Select value={moveToLocationId || "none"} onValueChange={(v) => setMoveToLocationId(v === "none" ? "" : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('noLocation')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('noLocation')}</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    <span className="flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> {loc.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveMenu(null)}>{t('cancel')}</Button>
            <Button onClick={handleMoveMenu} className="bg-stone-900 hover:bg-stone-800">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> {t('move')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Location Confirmation Dialog */}
      <Dialog open={!!locationToDelete} onOpenChange={(open) => !open && setLocationToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> {t('confirmDeleteLocation')}
            </DialogTitle>
            <DialogDescription>
              {t('confirmDeleteLocationDesc')} <strong>{locationToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 text-sm text-stone-600">
            {t('deleteLocationWarning')}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationToDelete(null)}>{t('cancel')}</Button>
            <Button onClick={handleDeleteLocation} variant="destructive">
              <Trash2 className="w-4 h-4 mr-2" /> {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slideshow QR Code Dialog */}
      <Dialog open={!!qrSlideshow} onOpenChange={(open) => !open && setQrSlideshow(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" /> QR Slideshow - {qrSlideshow?.name}
            </DialogTitle>
            <DialogDescription>{t('scanToViewSlideshow')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div ref={qrSlideshowRef} className="p-4 bg-white rounded-lg border shadow-sm">
              {qrSlideshow && <QRCodeSVG value={getSlideshowPublicUrl(qrSlideshow.id)} size={200} level="H" includeMargin />}
            </div>
            <p className="text-xs text-stone-500 text-center break-all max-w-[250px]">
              {qrSlideshow && getSlideshowPublicUrl(qrSlideshow.id)}
            </p>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-center">
            <Button variant="outline" onClick={() => { if (qrSlideshow) { navigator.clipboard.writeText(getSlideshowPublicUrl(qrSlideshow.id)); toast.success("Link copiado"); } }} className="flex-1">
              <Copy className="w-4 h-4 mr-2" /> Copiar Link
            </Button>
            <Button onClick={downloadSlideshowQR} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" /> Descargar QR
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Slideshow Dialog */}
      <Dialog open={!!editingSlideshow} onOpenChange={(open) => !open && setEditingSlideshow(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" /> {t('editSlideshow')}
            </DialogTitle>
            <DialogDescription>{t('modifySlideshowConfig')}</DialogDescription>
          </DialogHeader>
          {editingSlideshow && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('slideshowName')}</Label>
                  <Input
                    value={editingSlideshow.name}
                    onChange={(e) => setEditingSlideshow({...editingSlideshow, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación (opcional)</Label>
                  <Select 
                    value={editingSlideshow.location_id || "none"} 
                    onValueChange={(v) => setEditingSlideshow({...editingSlideshow, location_id: v === "none" ? null : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin ubicación" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Sin ubicación --</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          <span className="flex items-center gap-2">
                            <MapPin className="w-3 h-3" /> {loc.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Duración Global (segundos)
                  </Label>
                  <div className="flex items-center gap-3">
                    <Slider
                      value={[editingSlideshow.global_duration || 10]}
                      onValueChange={([v]) => setEditingSlideshow({...editingSlideshow, global_duration: v})}
                      min={5}
                      max={60}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-8">{editingSlideshow.global_duration || 10}s</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Efecto de Transición</Label>
                  <Select 
                    value={editingSlideshow.transition_effect || "fade"} 
                    onValueChange={(v) => setEditingSlideshow({...editingSlideshow, transition_effect: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fade">Fade (Desvanecer)</SelectItem>
                      <SelectItem value="slide">Slide (Deslizar)</SelectItem>
                      <SelectItem value="none">Sin transición</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Available menus to add */}
              <div className="space-y-2">
                <Label>{t('addMenus')}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg bg-stone-50">
                  {menus.filter(m => !editingSlideshow.menus?.find(s => s.menu_id === m.id)).map((menu) => (
                    <button
                      key={menu.id}
                      type="button"
                      onClick={() => setEditingSlideshow({
                        ...editingSlideshow,
                        menus: [...(editingSlideshow.menus || []), { menu_id: menu.id, duration: null }]
                      })}
                      className="p-2 rounded-lg border bg-white hover:bg-blue-50 hover:border-blue-300 text-left transition-all text-sm"
                    >
                      <div className="font-medium truncate">{menu.name}</div>
                    </button>
                  ))}
                  {menus.filter(m => !editingSlideshow.menus?.find(s => s.menu_id === m.id)).length === 0 && (
                    <p className="col-span-full text-sm text-stone-400 text-center py-2">
                      {language === 'en' ? 'All menus are in the slideshow' : language === 'pt' ? 'Todos os menus estão no slideshow' : language === 'ht' ? 'Tout meni yo nan slideshow la' : 'Todos los menús están en el slideshow'}
                    </p>
                  )}
                </div>
              </div>

              {/* Current menus in slideshow */}
              <div className="space-y-2">
                <Label>{t('menusInSlideshow')} ({editingSlideshow.menus?.length || 0})</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                  {!editingSlideshow.menus?.length ? (
                    <p className="text-sm text-stone-400 text-center py-4">
                      {language === 'en' ? 'No menus in this slideshow' : language === 'pt' ? 'Não há menus neste slideshow' : language === 'ht' ? 'Pa gen meni nan slideshow sa a' : 'No hay menús en este slideshow'}
                    </p>
                  ) : (
                    editingSlideshow.menus.map((menuItem, index) => {
                      const menu = menus.find(m => m.id === menuItem.menu_id);
                      return menu ? (
                        <div key={menuItem.menu_id} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex flex-col gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => {
                                if (index === 0) return;
                                const newMenus = [...editingSlideshow.menus];
                                [newMenus[index], newMenus[index - 1]] = [newMenus[index - 1], newMenus[index]];
                                setEditingSlideshow({...editingSlideshow, menus: newMenus});
                              }}
                              disabled={index === 0}
                            >
                              <ChevronRight className="w-3 h-3 rotate-[-90deg]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => {
                                if (index === editingSlideshow.menus.length - 1) return;
                                const newMenus = [...editingSlideshow.menus];
                                [newMenus[index], newMenus[index + 1]] = [newMenus[index + 1], newMenus[index]];
                                setEditingSlideshow({...editingSlideshow, menus: newMenus});
                              }}
                              disabled={index === editingSlideshow.menus.length - 1}
                            >
                              <ChevronRight className="w-3 h-3 rotate-90" />
                            </Button>
                          </div>
                          <GripVertical className="w-4 h-4 text-stone-400" />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{menu.name}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder="Global"
                              value={menuItem.duration || ""}
                              onChange={(e) => {
                                const newMenus = editingSlideshow.menus.map((m, i) => 
                                  i === index ? { ...m, duration: parseInt(e.target.value) || null } : m
                                );
                                setEditingSlideshow({...editingSlideshow, menus: newMenus});
                              }}
                              className="w-20 h-7 text-xs"
                            />
                            <span className="text-xs text-stone-500">seg</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                            onClick={() => {
                              const newMenus = editingSlideshow.menus.filter((_, i) => i !== index);
                              setEditingSlideshow({...editingSlideshow, menus: newMenus});
                            }}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : null;
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSlideshow(null)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateSlideshow} className="bg-blue-600 hover:bg-blue-700">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Configuration Dialog */}
      <Dialog open={!!scheduleDialog} onOpenChange={(open) => !open && setScheduleDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" /> {t('configureSchedule')}
            </DialogTitle>
            <DialogDescription>
              Define cuándo se mostrará este {scheduleDialog?.type === 'menu' ? 'menú' : 'slideshow'}
            </DialogDescription>
          </DialogHeader>
          {scheduleDialog && (
            <div className="space-y-4 py-4">
              {/* Enable/Disable */}
              <div className="flex items-center gap-3">
                <Checkbox
                  id="schedule-enabled"
                  checked={scheduleDialog.schedule?.enabled || false}
                  onCheckedChange={(checked) => setScheduleDialog({
                    ...scheduleDialog,
                    schedule: { ...scheduleDialog.schedule, enabled: checked }
                  })}
                />
                <Label htmlFor="schedule-enabled" className="cursor-pointer">
                  Activar programación de horario
                </Label>
              </div>

              {scheduleDialog.schedule?.enabled && (
                <>
                  {/* Day type */}
                  <div className="space-y-2">
                    <Label>Tipo de día</Label>
                    <Select
                      value={scheduleDialog.schedule?.day_type || 'weekdays'}
                      onValueChange={(v) => setScheduleDialog({
                        ...scheduleDialog,
                        schedule: { ...scheduleDialog.schedule, day_type: v, days: [] }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekdays">Días de la semana</SelectItem>
                        <SelectItem value="specific_dates">Fechas específicas del mes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Days selection */}
                  {scheduleDialog.schedule?.day_type === 'weekdays' ? (
                    <div className="space-y-2">
                      <Label>Días de la semana</Label>
                      <div className="flex flex-wrap gap-2">
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                          const dayNames = {
                            monday: 'Lun', tuesday: 'Mar', wednesday: 'Mié',
                            thursday: 'Jue', friday: 'Vie', saturday: 'Sáb', sunday: 'Dom'
                          };
                          const isSelected = scheduleDialog.schedule?.days?.includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = scheduleDialog.schedule?.days || [];
                                setScheduleDialog({
                                  ...scheduleDialog,
                                  schedule: {
                                    ...scheduleDialog.schedule,
                                    days: isSelected ? days.filter(d => d !== day) : [...days, day]
                                  }
                                });
                              }}
                              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                isSelected 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                              }`}
                            >
                              {dayNames[day]}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setScheduleDialog({
                            ...scheduleDialog,
                            schedule: { ...scheduleDialog.schedule, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] }
                          })}
                        >
                          Lun-Vie
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setScheduleDialog({
                            ...scheduleDialog,
                            schedule: { ...scheduleDialog.schedule, days: ['saturday', 'sunday'] }
                          })}
                        >
                          Sáb-Dom
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setScheduleDialog({
                            ...scheduleDialog,
                            schedule: { ...scheduleDialog.schedule, days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] }
                          })}
                        >
                          Todos
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Días del mes</Label>
                      <div className="grid grid-cols-7 gap-1 max-h-40 overflow-y-auto p-2 border rounded-lg">
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                          const dayStr = String(day);
                          const isSelected = scheduleDialog.schedule?.days?.includes(dayStr);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => {
                                const days = scheduleDialog.schedule?.days || [];
                                setScheduleDialog({
                                  ...scheduleDialog,
                                  schedule: {
                                    ...scheduleDialog.schedule,
                                    days: isSelected ? days.filter(d => d !== dayStr) : [...days, dayStr]
                                  }
                                });
                              }}
                              className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                                isSelected 
                                  ? 'bg-blue-600 text-white' 
                                  : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                              }`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Time range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Hora inicio</Label>
                      <Input
                        type="time"
                        value={scheduleDialog.schedule?.start_time || '00:00'}
                        onChange={(e) => setScheduleDialog({
                          ...scheduleDialog,
                          schedule: { ...scheduleDialog.schedule, start_time: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hora fin</Label>
                      <Input
                        type="time"
                        value={scheduleDialog.schedule?.end_time || '23:59'}
                        onChange={(e) => setScheduleDialog({
                          ...scheduleDialog,
                          schedule: { ...scheduleDialog.schedule, end_time: e.target.value }
                        })}
                      />
                    </div>
                  </div>

                  {/* Fallback message */}
                  <div className="space-y-2">
                    <Label>Mensaje fuera de horario</Label>
                    <Input
                      value={scheduleDialog.schedule?.fallback_message || ''}
                      onChange={(e) => setScheduleDialog({
                        ...scheduleDialog,
                        schedule: { ...scheduleDialog.schedule, fallback_message: e.target.value }
                      })}
                      placeholder="Vuelve pronto"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSchedule} className="bg-blue-600 hover:bg-blue-700">
              {t('saveSchedule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
